import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, Shield, User, Mail } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Users = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('users.noPermission'));
      } else {
        toast.error(t('users.loadError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        email: formData.email,
        name: formData.name || null,
        role: formData.role
      };
      
      if (formData.password) {
        dataToSend.password = formData.password;
      }

      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, dataToSend);
        toast.success(t('users.updateSuccess'));
      } else {
        if (!formData.password) {
          toast.error(t('users.passwordRequired'));
          return;
        }
        dataToSend.password = formData.password;
        await axios.post(`${API}/users`, dataToSend);
        toast.success(t('users.createSuccess'));
      }
      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('users.saveError'));
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name || '',
      role: user.role
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success(t('users.deleteSuccess'));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('users.deleteError'));
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'user'
    });
    setEditingUser(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const roleConfig = {
    admin: { label: t('users.roleAdmin'), color: 'bg-primary text-white' },
    user: { label: t('users.roleUser'), color: 'bg-slate-100 text-slate-600 border-slate-200' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900" data-testid="users-title">
            {t('users.title')}
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">{t('users.subtitle')}</p>
        </div>
        <Button 
          className="rounded-sm bg-primary hover:bg-[#D62839] shadow-none" 
          data-testid="create-user-button" 
          onClick={() => { resetForm(); setIsDialogOpen(true); }}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('users.newUser')}
        </Button>
      </div>

      {/* Users Grid */}
      {users.length === 0 ? (
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardContent className="py-12">
            <div className="text-center text-slate-400">
              <p>{t('users.noUsers')}</p>
              <p className="text-sm mt-1">{t('users.createFirst')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map((user) => (
            <Card key={user.id} className="border-slate-200 shadow-none rounded-sm hover:shadow-sm transition-shadow" data-testid="user-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.role === 'admin' ? 'bg-primary' : 'bg-slate-200'}`}>
                      {user.role === 'admin' ? (
                        <Shield className="w-5 h-5 text-white" />
                      ) : (
                        <User className="w-5 h-5 text-slate-600" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="font-heading text-base">
                        {user.name || t('users.unnamed')}
                      </CardTitle>
                      <Badge className={`rounded-full mt-1 ${roleConfig[user.role]?.color} border`}>
                        {roleConfig[user.role]?.label}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(user)}
                      data-testid={`edit-user-${user.id}`}
                      className="h-8 w-8 rounded-sm hover:bg-slate-100"
                    >
                      <Pencil className="w-4 h-4 text-slate-600" />
                    </Button>
                    {user.role !== 'admin' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setUserToDelete(user);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-user-${user.id}`}
                        className="h-8 w-8 rounded-sm hover:bg-slate-100"
                      >
                        <Trash2 className="w-4 h-4 text-slate-600" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-600 break-all">{user.email}</span>
                </div>
                <p className="text-xs text-slate-400">
                  {t('users.createdAt')}: {new Date(user.created_at).toLocaleDateString('de-DE')}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingUser ? t('users.editUser') : t('users.newUser')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('users.name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('users.namePlaceholder')}
                data-testid="user-name-input"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="user-email-input"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">
                {t('auth.password')} {editingUser ? `(${t('users.leaveEmptyToKeep')})` : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                placeholder={editingUser ? '••••••••' : ''}
                data-testid="user-password-input"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">{t('users.role')} *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger className="rounded-sm" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">{t('users.roleUser')}</SelectItem>
                  <SelectItem value="admin">{t('users.roleAdmin')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleDialogClose} className="rounded-sm">
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="rounded-sm bg-primary hover:bg-[#D62839]" data-testid="save-user-button">
                {editingUser ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('users.deleteUser')}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('users.deleteConfirm', { name: userToDelete?.name || userToDelete?.email })} {t('users.deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-primary hover:bg-[#D62839]">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
